import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ParticlesBg from 'particles-bg';
import Button from '../components/ui/Button';
import ContactModal from '../features/contact/components/ContactModal';
import PortfolioGrid from '../features/portfolio/components/PortfolioGrid';
import ServicesSection from '../components/sections/ServicesSection';

const HomePage = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  // Hero Section Animation
  const heroVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <ParticlesBg type="circle" bg={true} />
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        </div>

        {/* Hero Content */}
        <motion.div
          variants={heroVariants}
          initial="hidden"
          animate="visible"
          className="relative z-10 text-center px-4 max-w-4xl mx-auto"
        >
          <motion.div variants={itemVariants} className="mb-8">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-2">
              <span className="inline-block">NALDA</span>
            </h1>
            <p className="text-white/60 text-sm tracking-widest uppercase">
              naldadev@gmail.com
            </p>
          </motion.div>

          <motion.p
            variants={itemVariants}
            className="text-xl md:text-2xl text-white/80 mb-12 leading-relaxed"
          >
            혁신적인 웹과 모바일 솔루션을 제공하는
            <br />
            개발 전문 에이전시
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              variant="primary"
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="min-w-[200px]"
            >
              프로젝트 문의하기
            </Button>
            <a href="#portfolio">
              <Button variant="outline" size="lg" className="min-w-[200px] !text-white !border-white hover:!bg-white hover:!text-black">
                포트폴리오 보기
              </Button>
            </a>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="text-white/60"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Services Section */}
      <ServicesSection />

      {/* Portfolio Section */}
      <div id="portfolio">
        <PortfolioGrid />
      </div>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              프로젝트를 시작할 준비가 되셨나요?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              아이디어를 현실로 만들어드립니다. 지금 바로 문의해주세요.
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="bg-white !text-gray-900 hover:bg-gray-100"
            >
              지금 시작하기
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">NALDA</h3>
              <p className="text-gray-400">
                혁신적인 개발 솔루션을 제공하는 전문 에이전시
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">서비스</h4>
              <ul className="space-y-2 text-gray-400">
                <li>웹 개발</li>
                <li>모바일 앱 개발</li>
                <li>UI/UX 디자인</li>
                <li>컨설팅</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">회사</h4>
              <ul className="space-y-2 text-gray-400">
                <li>포트폴리오</li>
                <li>팀 소개</li>
                <li>채용</li>
                <li>블로그</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">연락처</h4>
              <ul className="space-y-2 text-gray-400">
                <li>naldadev@gmail.com</li>
                <li>서울특별시 강남구</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 NALDA. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </div>
  );
};

export default HomePage;